import type { ComponentProps, FC, ReactNode } from "react";

import classNames from "classnames";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import type { SafeHtmlAttributes } from "@jti/core-components";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { Skeleton } from "@jti/core-components/components/Skeleton";
import { SkeletonText } from "@jti/core-components/components/SkeletonText";

import { DetailsBar } from "../DetailsBar";
import { TierPointsProgressBar } from "../TierPointsProgressBar";
import type { TierItemProps } from "../TiersStatuses";
import { TiersStatuses } from "../TiersStatuses";

import styles from "./LoyaltyPointsStatus.module.scss";

const UPPER_ITEM_SKELETON_ROWS = 3;
const BOTTOM_ITEM_SKELETON_ROWS = 5;

type ItemDetails = {
  title: string;
  description: string;
  displayBarLeftDescription: string;
  detailsBarRightDescription: ReactNode;
  detailsBarButtonLabel?: string;
  detailsBarButtonUrl?: string;
};

export type LoyaltyPointsStatusUpperItemProps = Omit<SafeHtmlAttributes<HTMLDivElement>, "onToggle"> &
  ItemDetails & {
    points: number;
    pointsHistoryLabel: string;
    pointsHistoryUrl: string;
    pointsLabel: string;
    isLoading?: boolean;
    children?: ReactNode;
  };

export type LoyaltyPointsStatusBottomItemProps = Omit<SafeHtmlAttributes<HTMLDivElement>, "onToggle"> &
  ItemDetails & {
    cumulativePoints: number;
    date: string;
    userTier: string;
    progressBarPointsForGoldTier?: number;
    progressBarPointsForBlackTier?: number;
    progressBarPointsFooter: string;
    progressBarPointsTitle: string;
    tiersInfo: TierItemProps[];
    tierImage: string;
    detailsBarRightTitle: string;
    detailsBarGeneralTitle: string;
    isLoading?: boolean;
    children?: ReactNode;
  } & Pick<ComponentProps<typeof TierPointsProgressBar>, "tiersNames">;

export type LoyaltyPointsStatusProps = {
  children?: ReactNode;
};

const UpperItem: FC<LoyaltyPointsStatusUpperItemProps> = ({
  points,
  title,
  description,
  pointsLabel,
  displayBarLeftDescription,
  detailsBarRightDescription,
  detailsBarButtonLabel,
  detailsBarButtonUrl,
  pointsHistoryLabel,
  pointsHistoryUrl,
  isLoading,
  children,
  ...rest
}) => {
  const pointsSkeleton = (
    <div className={styles.points}>
      <Skeleton height="1lh" data-testid="loyalty-points-status-loading" />
    </div>
  );

  const upperItemTitle = (
    <div className={styles.leftBlockWrapper}>
      {isLoading ? pointsSkeleton : <p className={styles.points}>{points}</p>}
      <p className={styles.pointsLabel}>{pointsLabel}</p>
    </div>
  );

  const upperItemSubcontent = (
    <div className={styles.rightItems}>
      <p className={styles.rightTitle}>{title}</p>
      <p className={styles.rightDescription}>{description}</p>
    </div>
  );

  return (
    <div {...rest}>
      {children}
      <Card
        isCollapsible
        showSeparator
        data-testid="loyalty-points-status-upper-item"
        headerContent={
          <div className={styles.upperItemHeaderContentWrapper}>
            {upperItemTitle}
            {upperItemSubcontent}
          </div>
        }
        defaultCollapsed={true}
        variant="level-1"
      >
        <div className={styles.firstBlockContentWrapper}>
          {isLoading ? (
            <SkeletonText rows={UPPER_ITEM_SKELETON_ROWS} data-testid="loyalty-points-status-content-loading" />
          ) : (
            <>
              <div className={styles.mobileInfo}>
                <p className={styles.mobileInfoTitle}>{pointsLabel}</p>
                <p className={styles.mobileInfoDescription}>{description}</p>
              </div>
              <DetailsBar
                buttonVariant="primary"
                leftItem={points?.toString()}
                leftDescription={displayBarLeftDescription}
                rightTitle={points > 0 ? detailsBarRightDescription : null}
                buttonLabel={detailsBarButtonLabel}
                buttonUrl={detailsBarButtonUrl}
                buttonType="button"
              />
              <Button
                href={pointsHistoryUrl}
                variant="text-muted"
                size="sm"
                className={styles.pointsHistoryLink}
                iconAfter={LongArrowForwardIcon}
              >
                {pointsHistoryLabel}
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

const BottomItem: FC<LoyaltyPointsStatusBottomItemProps> = ({
  cumulativePoints,
  date,
  userTier,
  progressBarPointsTitle,
  progressBarPointsFooter,
  progressBarPointsForBlackTier,
  progressBarPointsForGoldTier,
  tiersInfo,
  tierImage,
  title,
  description,
  displayBarLeftDescription,
  detailsBarRightDescription,
  detailsBarRightTitle,
  detailsBarGeneralTitle,
  detailsBarButtonLabel,
  detailsBarButtonUrl,
  isLoading,
  children,
  tiersNames,
  ...rest
}) => {
  const bottomItemSubcontent = isLoading ? (
    <div className={classNames(styles.imageSkeleton, styles.image)}>
      <Skeleton height="100%" data-testid="loyalty-points-status-loading" />
    </div>
  ) : (
    <img className={styles.image} src={tierImage} alt={`${userTier}-tier-card`} />
  );

  return (
    <div {...rest}>
      {children}
      <Card
        variant="level-2"
        isCollapsible
        showSeparator
        nonCollapsibleContent={
          <div className={styles.tiersStatusesWrapper}>
            <TiersStatuses items={tiersInfo} isLoading={isLoading} />
          </div>
        }
        data-testid="loyalty-points-status-bottom-item"
        title={title}
        description={description}
        defaultCollapsed={true}
        headerContent={bottomItemSubcontent}
      >
        <div className={styles.secondBlockContentWrapper}>
          {isLoading ? (
            <SkeletonText rows={BOTTOM_ITEM_SKELETON_ROWS} data-testid="loyalty-points-status-content-loading" />
          ) : (
            <>
              <DetailsBar
                withDate={true}
                buttonVariant="primary"
                leftItem={date}
                leftDescription={displayBarLeftDescription}
                rightDescription={detailsBarRightDescription}
                rightTitle={detailsBarRightTitle}
                barTitle={detailsBarGeneralTitle}
                hideLeftBlock={userTier?.toLowerCase() === "silver"}
                buttonLabel={detailsBarButtonLabel}
                buttonUrl={detailsBarButtonUrl}
                buttonType="button"
              />
              <TierPointsProgressBar
                currentPoints={cumulativePoints}
                tier={userTier}
                pointsForGoldTier={progressBarPointsForGoldTier}
                pointsForBlackTier={progressBarPointsForBlackTier}
                pointsFooter={progressBarPointsFooter}
                pointsTitle={progressBarPointsTitle}
                tiersNames={tiersNames}
              />
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

const LoyaltyPointsStatusBase: FC<LoyaltyPointsStatusProps> = ({ children }) => (
  <div className={styles.loyaltyPointsStatus} data-testid="loyalty-points-status">
    {children}
  </div>
);

export const LoyaltyPointsStatus = Object.assign(LoyaltyPointsStatusBase, {
  UpperItem,
  BottomItem,
});
